import React from "react";
import { Section, Reveal, Glass } from "@/components/network/NetworkAtoms";

/**
 * Anonymised, illustrative deal shapes — the kind of thing the panel places. Clearly labelled as
 * illustrative: no invented named customers, no invented volumes.
 */
const STORIES = [
  {
    amount: "£18m", product: "Portfolio refinance",
    who: "Healthcare group, four sites",
    body: "Refinanced away from an incumbent bank that wanted the whole structure reworked. Placed with a single "
        + "lead funder on a seven-year term, with headroom for the fifth site.",
    beat: "Terms in 6 days",
  },
  {
    amount: "£4.2m", product: "Asset refinance + capex",
    who: "Industrial manufacturer, Midlands",
    body: "Released capital held in existing plant and funded a new line in the same facility. Security sat on the "
        + "assets rather than the trading business, which kept the covenant package light.",
    beat: "Terms in 31 hours",
  },
  {
    amount: "£1.2m", product: "Bridging",
    who: "Property investor, South East",
    body: "Auction purchase with 28 days to complete. Two funders responded inside 48 hours; the cheaper needed a "
        + "valuation the agent turned round in three days.",
    beat: "Completed with a week spare",
  },
  {
    amount: "£450k", product: "Invoice finance",
    who: "Recruitment group, North West",
    body: "Paying contractors weekly against 60-day client terms. A confidential receivables facility turned every "
        + "approved timesheet into same-day cash and scaled with the desk.",
    beat: "Live inside a week",
  },
];

export default function OutcomeStories({ stories, sector }) {
  const items = stories || STORIES;
  return (
    <Section
      testId="funding-stories"
      eyebrow="The shape of the work"
      title={sector ? `Four ${sector.short.toLowerCase()} cases, four different structures`
                    : "Four cases, four completely different structures"}
      lede="Anonymised, and illustrative of what the panel places week to week. They're here to show how differently
            the same requirement gets structured depending on the business behind it."
    >
      <div className="mt-12 grid md:grid-cols-2 gap-5">
        {items.map((s, i) => (
          <Reveal key={s.amount + s.product} delay={i * 0.07}>
            <Glass className="h-full p-7" testId={`story-${i}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-cabinet text-[30px] sm:text-[34px] leading-none font-bold text-white tracking-[-0.035em]">
                    {s.amount}
                  </div>
                  <div className="mt-2 text-[13px] font-medium text-[#7da9ff]">{s.product}</div>
                </div>
                <span className="rounded-full border border-[#22c55e]/30 bg-[#22c55e]/10 px-3 py-1 text-[11px] font-medium text-[#22c55e] whitespace-nowrap">
                  {s.beat}
                </span>
              </div>
              <div className="mt-5 text-[12.5px] uppercase tracking-[0.14em] text-slate-500">{s.who}</div>
              <p className="mt-3 text-[14.5px] leading-relaxed text-slate-300">{s.body}</p>
            </Glass>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <p className="mt-7 text-[11.5px] text-slate-500 max-w-2xl leading-relaxed">
          Illustrative of the deal types placed through our panel. Amounts, structures and timings vary with
          every business, and nothing here is a promise of what you'd be offered.
        </p>
      </Reveal>
    </Section>
  );
}
